'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Camera } from 'lucide-react';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  timezone: string;
  company: string;
  website: string;
}

const timezones = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Tokyo',
  'Australia/Sydney',
];

export default function ProfileSettings() {
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: 'Marcus',
      lastName: 'Allen',
      email: 'marcus@mailflow.io',
      timezone: 'America/New_York',
      company: 'MailFlow Inc.',
      website: 'https://mailflow.io',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    // Backend integration point: PATCH /api/users/profile
    setSaving(true);
    await new Promise((r) => setTimeout(r, 900));
    setSaving(false);
    toast.success('Profile updated successfully');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="card p-6 space-y-6">
        <h2 className="section-header">Profile Information</h2>

        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xl font-bold text-primary">MA</span>
            </div>
            <button
              type="button"
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-sm hover:opacity-90 transition-all"
            >
              <Camera size={12} className="text-white" />
            </button>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Profile Photo</p>
            <p className="text-xs text-muted-foreground mt-0.5">JPG or PNG, max 2MB</p>
          </div>
        </div>

        {/* Name */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-base" htmlFor="firstName">First Name</label>
            <input id="firstName" type="text" className="input-base" {...register('firstName', { required: 'First name is required' })} />
            {errors.firstName && <p className="text-xs text-red-600 mt-1">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="label-base" htmlFor="lastName">Last Name</label>
            <input id="lastName" type="text" className="input-base" {...register('lastName', { required: 'Last name is required' })} />
            {errors.lastName && <p className="text-xs text-red-600 mt-1">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="label-base" htmlFor="profileEmail">Email Address</label>
          <p className="text-xs text-muted-foreground mb-1">Used for account notifications and login</p>
          <input
            id="profileEmail"
            type="email"
            className="input-base"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' },
            })}
          />
          {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>}
        </div>

        {/* Company + Website */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-base" htmlFor="company">Company</label>
            <input id="company" type="text" className="input-base" {...register('company')} />
          </div>
          <div>
            <label className="label-base" htmlFor="website">Website</label>
            <input id="website" type="url" className="input-base" {...register('website')} />
          </div>
        </div>

        {/* Timezone */}
        <div>
          <label className="label-base" htmlFor="timezone">Timezone</label>
          <p className="text-xs text-muted-foreground mb-1">Affects scheduled send times and reporting</p>
          <select id="timezone" className="input-base" {...register('timezone')}>
            {timezones.map((tz) => (
              <option key={`tz-${tz}`} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Save bar */}
      <div className="flex items-center justify-between px-6 py-4 card">
        {isDirty && (
          <p className="text-sm text-amber-600 font-medium">You have unsaved changes</p>
        )}
        <div className="flex gap-3 ml-auto">
          <button type="button" className="btn-secondary">Discard Changes</button>
          <button type="submit" disabled={saving} className="btn-primary min-w-[120px]">
            {saving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
                </svg>
                Saving…
              </span>
            ) : 'Save Profile'}
          </button>
        </div>
      </div>
    </form>
  );
}